import React, { useState, useEffect } from 'react';
import Modal from './Modal';

const ImageViewer = ({ isOpen, onClose, imageUrl, filename }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isOpen && imageUrl) {
      setIsLoading(true);
      setError(false);

      // Pre-load the image
      const img = new Image();
      img.onload = () => setIsLoading(false);
      img.onerror = () => {
        setIsLoading(false);
        setError(true);
      };
      img.src = imageUrl;
    }
  }, [isOpen, imageUrl]);

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('ncrs_token');
      console.log('Image Download token check:', token ? 'Token found' : 'No token found');

      // Show loading state
      if (window.showToast) {
        window.showToast('Downloading image...', 'info');
      }

      // Create headers object - include auth if token exists
      const headers = {
        'Accept': '*/*'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Fetch the image with authentication
      const response = await fetch(imageUrl, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      // Get the blob data
      const blob = await response.blob();

      // Create blob URL and trigger download (without target='_blank')
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'image';
      link.style.display = 'none'; // Hide the link
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);

      if (window.showToast) {
        window.showToast('Image downloaded successfully', 'success');
      }
    } catch (error) {
      console.error('Image download error:', error);
      if (window.showToast) {
        window.showToast(`Failed to download image: ${error.message}`, 'error');
      }
    }
  };

  const handleOpenInNewTab = () => {
    window.open(imageUrl, '_blank');
  };

  // Header actions for the modal
  const headerActions = (
    <div className="flex space-x-2">
        <button
        onClick={handleOpenInNewTab}
        className="border border-primary text-primary px-3 py-2 text-sm rounded hover:bg-blue-50 flex items-center space-x-1"
        title="Open in New Tab"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
        <span className="hidden sm:inline">Open in New Tab</span>
      </button>
      <button
        onClick={handleDownload}
        className="btn-primary flex items-center space-x-2"
        title="Download image"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span className="hidden sm:inline">Download</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={filename || 'Image Viewer'}
      size="full"
      zIndex="z-60"
      headerActions={headerActions}
    >
      {/* Image Display - Full height now that toolbar is moved to header */}
      <div className="flex justify-center items-center bg-gray-50 rounded-lg" style={{ height: 'calc(100vh - 200px)' }}>
        {isLoading && (
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-gray-600">Loading image...</p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center space-y-4 text-red-600">
            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p>Failed to load image</p>
            <button
              onClick={() => window.open(imageUrl, '_blank')}
              className="text-blue-600 hover:text-blue-800 underline"
            >
              Try opening in new tab
            </button>
          </div>
        )}

        {!isLoading && !error && imageUrl && (
          <img
            src={imageUrl}
            alt={filename || 'Attachment'}
            className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
            onError={() => setError(true)}
          />
        )}
      </div>

      {/* Image Info */}
      {!isLoading && !error && (
        <div className="text-center text-sm text-gray-600 mt-2">
          <p>Click and drag to move • Right-click to save • Use browser zoom for closer inspection</p>
        </div>
      )}
    </Modal>
  );
};

export default ImageViewer;
